import L from 'leaflet'
import { useEffect } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import {
  MapContainer as ReactLeafletMapContainer,
  Marker as ReactLeafletMarker,
  Popup as ReactLeafletPopup,
  TileLayer as ReactLeafletTileLayer,
  useMap,
  useMapEvents,
} from 'react-leaflet'
import MarkerClusterGroup from 'react-leaflet-cluster'

import type { LocationListItem } from '../shared/api/types'
import { buildSearchParams } from '../shared/lib/search-params'

import icon from 'leaflet/dist/images/marker-icon.png'
import iconShadow from 'leaflet/dist/images/marker-shadow.png'

const defaultCenter: [number, number] = [46.1512, 14.9955]

const defaultIcon = L.icon({
  iconUrl: icon,
  shadowUrl: iconShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
})

L.Marker.prototype.options.icon = defaultIcon

const MapContainer = ReactLeafletMapContainer as any
const Marker = ReactLeafletMarker as any
const Popup = ReactLeafletPopup as any
const TileLayer = ReactLeafletTileLayer as any

function serializeBounds(bounds: any) {
  return [bounds.getWest(), bounds.getSouth(), bounds.getEast(), bounds.getNorth()]
    .map((value) => value.toFixed(6))
    .join(',')
}

function MapBoundsUpdater() {
  const [searchParams, setSearchParams] = useSearchParams()
  const map = useMapEvents({
    moveend: () => {
      const bbox = serializeBounds(map.getBounds())
      if (searchParams.get('bbox') === bbox) return

      setSearchParams(buildSearchParams(searchParams, { bbox }), { replace: true })
    },
  })

  useEffect(() => {
    const bbox = serializeBounds(map.getBounds())
    if (searchParams.get('bbox') === bbox) return

    setSearchParams(buildSearchParams(searchParams, { bbox }), { replace: true })
  }, [map, searchParams, setSearchParams])

  return null
}

function FitToLocations({ locations }: { locations: LocationListItem[] }) {
  const map = useMap()

  useEffect(() => {
    const points = locations
      .filter((location) => location.latitude !== null && location.longitude !== null)
      .map((location) => [location.latitude!, location.longitude!] as [number, number])

    if (!points.length) return
    if (points.length === 1) {
      map.setView(points[0], 13)
      return
    }

    map.fitBounds(points, { padding: [32, 32] })
  }, [locations, map])

  return null
}

export function MapView({
  locations,
  syncBounds = false,
  fitToLocations = false,
}: {
  locations: LocationListItem[]
  syncBounds?: boolean
  fitToLocations?: boolean
}) {
  return (
    <MapContainer center={defaultCenter} zoom={8} scrollWheelZoom className="h-full w-full">
      <TileLayer
        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
      />
      {syncBounds ? <MapBoundsUpdater /> : null}
      {fitToLocations ? <FitToLocations locations={locations} /> : null}
      <MarkerClusterGroup chunkedLoading>
        {locations.map((location) => {
          if (location.latitude === null || location.longitude === null) return null

          return (
            <Marker key={location.id} position={[location.latitude, location.longitude]}>
              <Popup>
                <div className="min-w-[220px] space-y-2">
                  <div>
                    <h3 className="text-base font-semibold text-slate-900">{location.title}</h3>
                    <p className="mt-1 text-sm leading-5 text-slate-600">
                      {location.short_description || location.region_code || 'Lokacija brez opisa'}
                    </p>
                  </div>
                  <Link
                    to={`/locations/${location.slug}`}
                    className="block rounded-xl bg-emerald-700 px-3 py-2 text-center text-sm font-semibold text-white transition hover:bg-emerald-800"
                  >
                    Poglej detajle
                  </Link>
                </div>
              </Popup>
            </Marker>
          )
        })}
      </MarkerClusterGroup>
    </MapContainer>
  )
}
